import SwimFormField from '@/components/shared/form/SwimFormField'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Form } from '@/components/ui/form'
import { Separator } from '@/components/ui/separator'
import { useAuthWithProfile } from '@/hooks/useAuth'
import { toast } from '@/hooks/useToast'
import { formatDate } from '@/lib/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute } from '@tanstack/react-router'
import debounce from 'lodash-es/debounce'
import { Mail, Phone } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

export const Route = createFileRoute('/_protected/dashboard/profile')({
  component: ProfileComponent,
})

const profileSchema = z.object({
  name_first: z.string().min(1, 'First name is required'),
  name_last: z.string().min(1, 'Last name is required'),
  email: z.string().email(),
  phone: z.string().optional(),
  enable_email_notifications: z.boolean(),
  enable_sms_notifications: z.boolean(),
  enable_marketing: z.boolean(),
})

function ProfileComponent() {
  const { profile, updateProfileMutation } = useAuthWithProfile()

  const form = useForm<z.infer<typeof profileSchema>>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name_first: profile.name_first || '',
      name_last: profile.name_last || '',
      email: profile.email || '',
      phone: profile.phone || '',
      enable_email_notifications: profile.enable_email_notifications ?? true,
      enable_sms_notifications: profile.enable_sms_notifications ?? false,
      enable_marketing: profile.enable_marketing ?? false,
    }
  })

  const isFirstRun = useRef(true)

  useEffect(() => {
    const debouncedUpdate = debounce(async (values) => {
      if (form.formState.isDirty && form.formState.isValid) {
        console.log('Updating profile with values:', values)
        await updateProfileMutation.mutateAsync(values, {
          onSuccess: () => {
            form.reset(values)
          },
          onError: () => {
            toast({
              title: 'Error',
              description: 'Failed to update profile',
              variant: 'destructive',
            })
          }
        })
      }
    }, 500) // 500ms debounce

    const subscription = form.watch((values) => {
      if (isFirstRun.current) {
        isFirstRun.current = false
        return
      }
      debouncedUpdate(values)
    })

    return () => {
      subscription.unsubscribe()
      debouncedUpdate.cancel()
    }
  }, [form, updateProfileMutation])

  // Get initials from name for avatar fallback
  const getInitials = () => {
    const first = profile.name_first?.charAt(0) || ''
    const last = profile.name_last?.charAt(0) || ''
    return (first + last).toUpperCase()
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Profile</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-col items-center text-center pb-2">
            <Avatar className="h-24 w-24 mb-4">
              <AvatarImage src={profile.avatar || undefined} />
              <AvatarFallback className="bg-primary text-xl">{getInitials()}</AvatarFallback>
            </Avatar>
            <CardTitle className="text-xl">{profile.name_first} {profile.name_last}</CardTitle>
            <CardDescription className="flex items-center gap-2 justify-center">
              <Mail className="h-3 w-3" /> {profile.email}
            </CardDescription>
            {profile.phone && (
              <CardDescription className="flex items-center gap-2 justify-center">
                <Phone className="h-3 w-3" /> {profile.phone}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="pb-2 text-center">
            <div className="text-sm text-muted-foreground">
              Member since {formatDate(profile.created_at)}
            </div>
            {profile.swimhomes_admin && (
              <div className="mt-2">
                <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-md">
                  Admin
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Profile Edit Form */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
            <CardDescription>Update your personal information and preferences</CardDescription>
          </CardHeader>
          <Form {...form}>
            <form>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SwimFormField
                    schema={profileSchema}
                    fieldName="name_first"
                    label="First Name"
                  />
                  <SwimFormField
                    schema={profileSchema}
                    fieldName="name_last"
                    label="Last Name"
                  />
                </div>

                <SwimFormField
                  schema={profileSchema}
                  label='Email Address'
                  fieldName="email"
                  disabled={true}
                  required={false}
                />

                <SwimFormField
                  schema={profileSchema}
                  fieldName="phone"
                  label="Phone Number"
                  placeholder='+1 (555) 000-0000'
                />

                <Separator className="my-4" />
                <h3 className="text-lg font-medium">Notification Preferences</h3>

                <SwimFormField
                  schema={profileSchema}
                  fieldName="enable_email_notifications"
                  label="Email Notifications"
                  description="Receive email notifications about your account"
                />
                <SwimFormField
                  schema={profileSchema}
                  fieldName="enable_sms_notifications"
                  label="SMS Notifications"
                  description="Receive text messages for important updates"
                />
                <SwimFormField
                  schema={profileSchema}
                  fieldName="enable_marketing"
                  label="Marketing Communications"
                  description="Receive information about new features and offers"
                />
              </CardContent>
            </form>
          </Form>
        </Card>
      </div>
    </div>
  )
}
